import { useMemo, useState } from 'react';
import { Button } from '@territory/ui';
import { API, apiUrl } from '@territory/client/routes';
import { RequestAccessForm } from './RequestAccessForm';
import { PinLogin } from './PinLogin';
import { useInAppBrowserGuard } from './wedgeInAppGuard';
import { isNativeIOS } from './native/nativeBridge';
import { BRAND, brandScreen, BrandMark, BrandWordmark, BrandStyleTag } from './wedgeBrand';

const REASONS: Record<string, string> = {
  auth_failed:
    'Sign-in failed. If you\'re using a private or incognito window, try opening PREP in a regular browser window instead.',
  invalid_state:
    'Your sign-in session expired or could not be verified. Please start over.',
  session_expired:
    'Your session timed out. Sign back in to pick up where you left off.',
  denied:
    'Sign-in could not complete. This can happen in a private or incognito window. Try a regular browser window, or request access below.',
  /* 2026-08-10 (SIWA pending-account incident) · the account authenticated
     fine but is not activated · retrying the same provider can never fix it,
     so the copy must say so instead of coaching a retry loop. */
  pending:
    'Your sign-in worked, but this account is not switched on yet. Reach out to the person who set it up for you, or request access below and we will sort it quickly.',
};

/* iOS shell copy. The web REASONS blame private/incognito windows and point at
   "request access below" · both wrong inside the Capacitor shell, where the
   only working ways back in are Continue with Apple (on the sign-in screen)
   and the PIN. Say exactly that and nothing more. */
const NATIVE_REASONS: Record<string, string> = {
  auth_failed:
    'Sign-in could not complete. Go back and try Continue with Apple, or sign in with your PIN.',
  invalid_state:
    'Your sign-in session expired or could not be verified. Go back and start over.',
  session_expired:
    'Your session timed out. Sign back in to pick up where you left off.',
  denied:
    'Sign-in could not complete. Go back and try Continue with Apple, or sign in with your PIN.',
  pending:
    'Your sign-in worked, but this account is not switched on yet. Ask the person who set it up to activate it, or sign in with a different account.',
};

/**
 * Terminal screen for a rejected / errored login. CRITICAL: never auto-
 * redirects (a non-@repvantage.io user would otherwise loop back to Google
 * forever). The retry is user-initiated.
 *
 * 2026-09-28 · the Y Combinator reviewer entry is gone (YC archived, Luke
 * ruling); the generic access-code entry was already retired 2026-06-14.
 *
 * It also carries the EXTERNAL request-access form (shared RequestAccessForm):
 * a non-RepVantage visitor who gets bounced here can still ask for a free PREP
 * account, the same as from the WedgeLanding chooser.
 */
export function AccessDenied({ reason }: { reason?: string | null }) {
  const [view, setView] = useState<'denied' | 'form' | 'pin'>('denied');
  const inApp = useInAppBrowserGuard('access_denied');
  /* App Store P0-3 · isNativeIOS() is false in every browser, so every native
     branch below is dead code on wedge.repvantage.io and the web screen
     renders exactly what it always did. */
  const nativeIOS = useMemo(() => {
    try { return isNativeIOS(); } catch { return false; }
  }, []);
  const msg = nativeIOS
    ? (reason && NATIVE_REASONS[reason]) || NATIVE_REASONS.denied
    : (reason && REASONS[reason]) || REASONS.denied;
  /* When the reason is a recoverable session expiry (or no reason at all —
     a fresh visitor whose cookie just couldn't be verified), the headline
     should not accuse them of being on the wrong account. */
  const headline =
    reason === 'session_expired' ? 'Sign back in' :
    reason === 'invalid_state' ? 'Sign back in' :
    reason === 'pending' ? 'Account not active yet' :
    'Sign-in problem';

  return (
    <div style={{ ...brandScreen, gap: 14 }}>
      <BrandStyleTag />
      <BrandMark size={70} />
      <BrandWordmark size={18} />

      {view === 'denied' && (
        <>
          <div style={{ fontSize: 18, fontWeight: 700, color: BRAND.text }}>{headline}</div>
          <div style={{ fontSize: 14, color: BRAND.textSec, maxWidth: 360, lineHeight: 1.5 }}>{msg}</div>
          {nativeIOS ? (
            /* App Store P0-3 · a bounced rep in the shell used to face only
               dead controls: Google OAuth top-level navigation (Google blocks
               OAuth in embedded webviews), a request-access form that emails a
               Safari link, and a YC redeem whose Google path is equally dead
               inside capacitor://localhost. Force-quitting was the only way
               out. The shell instead gets the two paths that actually work:
               back to the sign-in screen (whose primary action is the native
               Continue with Apple sheet) and the in-app PIN. The Back button
               is a full navigation, same as PinLogin's own success path, so
               the ?error= param is gone when WedgeAuthGate re-evaluates. */
            <>
              <Button
                appearance="wedge-auth" authSize="native"
                onClick={() => { window.location.replace('/'); }} style={{ marginTop: 6, maxWidth: 360 }}
              >
                Back to sign in
              </Button>
              <Button
                appearance="wedge-auth" variant="ghost" authSize="native-link"
                onClick={() => setView('pin')} style={{ width: 'auto', marginTop: 0 }}
              >
                Sign in with your PIN
              </Button>
            </>
          ) : (
          <>
          {inApp.notice}
        <Button
            appearance="wedge-auth" authSize="compact"
            onClick={() => {
              inApp.guardGoogle(`${apiUrl(API.AUTH.GOOGLE)}?app=wedge`);
            }} style={{ marginTop: 6, width: 'auto' }}
          >
            Try again with my work account
          </Button>
          <div
            style={{
              marginTop: 18,
              paddingTop: 18,
              borderTop: `1px solid ${BRAND.border}`,
              maxWidth: 360,
              width: '100%',
            }}
          >
            <div style={{ fontSize: 13.5, color: BRAND.textSec, lineHeight: 1.5, marginBottom: 10 }}>
              Not with RepVantage? Request access and we'll email you a one-tap sign-in link.
            </div>
            <Button
              appearance="wedge-auth" variant="secondary" authSize="request"
              onClick={() => setView('form')} style={{ width: 'auto' }}
            >
              Request access &rarr;
            </Button>
          </div>
          </>
          )}
        </>
      )}

      {view === 'form' && <RequestAccessForm onBack={() => setView('denied')} />}
      {/* Reachable only from the native block above · nothing on web calls
          setView('pin'), so the web screen keeps exactly the views it had. */}
      {view === 'pin' && (
        <div style={{ width: '100%', maxWidth: 360 }}>
          <PinLogin onBack={() => setView('denied')} />
        </div>
      )}
    </div>
  );
}
